<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import ReviewsEmptyList from "@apps/reviews/components/ReviewsEmptyList.svelte";
    import ReviewsList from "@apps/reviews/components/ReviewsList/ListingLayout/ReviewsList.svelte";
    import ReviewsMasonryList from "@apps/reviews/components/ReviewsList/ReviewsMasonryLayout/ReviewsMasonryList.svelte";
    import ReviewsTextInput from "@apps/reviews/components/ReviewsTextInput/ReviewsTextInput.svelte";
    import {
        REVIEWS_WIDGET_LAYOUT,
        REVIEW_TYPE,
    } from "@apps/reviews/constants";
    import ReviewsSortingOptionsEntity from "@apps/reviews/entities/ReviewsSortingOptionsEntity";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { reviewsFormModalToken } from "@apps/reviews/tokens";
    import ProductReviewsQuery from "@apps/reviews/widgets/reviews_page_widget/queries/ProductReviewsQuery";
    import ProductReviewsService from "@apps/reviews/widgets/reviews_page_widget/services/ProductReviewsService";
    import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import {
        BehaviorSubject,
        combineLatest,
        debounceTime,
        lastValueFrom,
        map,
        startWith,
        tap,
    } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import Button from "@components/Buttons/Button.svelte";
    import Divider from "@components/Divider.svelte";
    import GrowaveBrand from "@components/GrowaveBrand/GrowaveBrand.svelte";
    import Icon from "@components/Icon.svelte";
    import ImageViewer from "@components/ImageViewer/ImageViewer.svelte";
    import ImageViewerController from "@components/ImageViewer/ImageViewerController";
    import LoadMore from "@components/LoadMore.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Sort from "@components/Sort.svelte";
    import T from "@components/T.svelte";
    import Tabs from "@components/Tabs/Tabs.svelte";
    import TabsManager from "@components/Tabs/TabsManager";
    import TabsMenu from "@components/Tabs/TabsMenu.svelte";
    import TabsPanel from "@components/Tabs/TabsPanel.svelte";

    import { hasNextPage } from "../../../../utils/paginationUtils/paginationUtils";
    import ReviewsEmptyListBackgroundIcon from "../../components/icons/ReviewsEmptyListBackgroundIcon.svelte";
    import ReviewsTheme from "../../components/ReviewsTheme/ReviewsTheme.svelte";
    import { LEAVE_REVIEW_SOURCE } from "../review_form_modal_widget/constants";
    import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
    import ReviewLayoutSwitcher from "./components/ReviewLayoutSwitcher.svelte";
    import { REVIEWS_PAGE_TABS } from "./constants";
    import SiteReviewsQuery from "./queries/SiteReviewsQuery";
    import SiteReviewsService from "./services/SiteReviewsService";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let productReviewsQuery: ProductReviewsQuery;
    export let productReviewsService: ProductReviewsService;
    export let siteReviewsQuery: SiteReviewsQuery;
    export let siteReviewsService: SiteReviewsService;
    export let growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    export let modalManager: ModalManager;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let reviewsPerPage: number;
    export let afterWidgetInject: () => void;

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();
    const watermarkSettings$ =
        growaveStorefrontValuesService.selectWatermarkSettings();

    const productReviews$ = productReviewsQuery.all$;
    const siteReviews$ = siteReviewsQuery.all$;
    const productReviewsStatus$ = productReviewsQuery.status$;
    const siteReviewsStatus$ = siteReviewsQuery.status$;
    const productReviewsPaginationMeta$ = productReviewsQuery.paginationMeta$;
    const siteReviewsPaginationMeta$ = siteReviewsQuery.paginationMeta$;

    const tabsManager = new TabsManager();
    const openedTab$ = tabsManager.openedTab$;
    const imageViewerController = new ImageViewerController();
    const i18nModule = container.resolve(I18nModule);

    const t_product_reviews$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsPageWidget",
        "product_reviews"
    );
    const t_shop_reviews$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsPageWidget",
        "shop_reviews"
    );
    const tabs$ = combineLatest([
        productReviewsPaginationMeta$,
        siteReviewsPaginationMeta$,
        t_product_reviews$,
        t_shop_reviews$,
    ]).pipe(
        map(
            ([
                productReviewsPaginationMeta,
                siteReviewsPaginationMeta,
                t_product_reviews,
                t_shop_reviews,
            ]) => [
                {
                    name: REVIEWS_PAGE_TABS.PRODUCT_REVIEWS,
                    label: t_product_reviews,
                    tag: Number(productReviewsPaginationMeta.totalCount),
                },
                {
                    name: REVIEWS_PAGE_TABS.SITE_REVIEWS,
                    label: t_shop_reviews,
                    tag: Number(siteReviewsPaginationMeta.totalCount),
                },
            ]
        ),
        startWith([])
    );

    let search$ = new BehaviorSubject<string>("");
    let currentSort$ = new BehaviorSubject<ReviewsSortingOptionsEntity>(
        "mostRecent"
    );

    let layout = REVIEWS_WIDGET_LAYOUT.LISTING_LAYOUT;
    const handleChangeLayoutView = (event: {
        detail: { value: REVIEWS_WIDGET_LAYOUT };
    }) => {
        layout = event.detail.value;
    };

    const handleProductReviewsLoadMore = () => {
        lastValueFrom(
            productReviewsService.loadMoreReviews({
                offset:
                    $productReviewsPaginationMeta$.perPage +
                    $productReviewsPaginationMeta$.currentOffset,
                sortingOptions: [$currentSort$],
                search: $search$,
                onlyProductReviews: true,
                onlyShopReviews: false,
                perPage: reviewsPerPage,
            })
        );
    };
    const handleSiteReviewsLoadMore = () => {
        lastValueFrom(
            siteReviewsService.loadMoreReviews({
                offset:
                    $siteReviewsPaginationMeta$.perPage +
                    $siteReviewsPaginationMeta$.currentOffset,
                sortingOptions: [$currentSort$],
                search: $search$,
                onlyProductReviews: false,
                onlyShopReviews: true,
                perPage: reviewsPerPage,
            })
        );
    };
    const handleSearch = (event: CustomEvent) => {
        search$.next(event.detail.value);
    };

    const handleSortReview = (sort: ReviewsSortingOptionsEntity) => {
        currentSort$.next(sort);
    };

    onMount(() => {
        tabsManager.openTab(REVIEWS_PAGE_TABS.PRODUCT_REVIEWS);

        const getProductReviewsSubscription = combineLatest([
            currentSort$,
            search$.pipe(debounceTime(400)),
            openedTab$,
        ]).subscribe(([currentSort, search]) => {
            lastValueFrom(
                productReviewsService.getReviews({
                    sortingOptions: [currentSort],
                    search: search,
                    onlyProductReviews: true,
                    onlyShopReviews: false,
                    perPage: reviewsPerPage,
                })
            );
        });

        const getSiteReviewsSubscription = combineLatest([
            currentSort$,
            search$.pipe(debounceTime(400)),
            openedTab$,
        ]).subscribe(([currentSort, search]) => {
            lastValueFrom(
                siteReviewsService.getReviews({
                    sortingOptions: [currentSort],
                    search: search,
                    onlyProductReviews: false,
                    onlyShopReviews: true,
                    perPage: reviewsPerPage,
                })
            );
        });

        const reviewsBrandingSubscription = reviewsBranding$.subscribe(
            (branding) => {
                if (branding) {
                    layout =
                        branding.reviewsWidgetsSettings.reviewsWidgetLayout;
                }
            }
        );

        afterWidgetInject();

        return () => {
            getProductReviewsSubscription.unsubscribe();
            getSiteReviewsSubscription.unsubscribe();
            reviewsBrandingSubscription.unsubscribe();
        };
    });

    const handleOpenReviewsGalleryModal = (
        event: CustomEvent<{ reviewId: number; activeImageIndex?: number }>
    ) => {
        const reviewId = event.detail.reviewId;
        const activeImageIndex = event.detail.activeImageIndex;
        reviewsGalleryManager.openReviewModal({
            reviews$:
                $openedTab$ === REVIEWS_PAGE_TABS.PRODUCT_REVIEWS
                    ? productReviews$
                    : siteReviews$,
            activeReviewId: reviewId,
            activeImageIndex: activeImageIndex,
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET,
        });
    };

    const handleOpenReviewsFormModal = () => {
        modalManager.openModal(reviewsFormModalToken, undefined, {
            source: LEAVE_REVIEW_SOURCE.REVIEWS_PAGE_WIDGET,
            reviewType: REVIEW_TYPE.SITE_REVIEW,
            onLeaveReviewCb: (review) => {
                siteReviewsService.appendReview(review);
            },
        });
    };

    const sortItems$ = reviewsSettings$.pipe(
        tap((settings) => {
            if (settings?.isReviewsPinningEnabled) {
                currentSort$.next("mostRelevant");
            }
        }),
        map((settings) => {
            const sortItems = [
                {
                    value: "mostRecent",
                    labelTranslationKey: "sort_most_recent",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                },
                {
                    value: "highestRating",
                    labelTranslationKey: "sort_highest_rating",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                },
                {
                    value: "lowestRating",
                    labelTranslationKey: "sort_lowest_rating",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                },
                {
                    value: "withPhotos",
                    labelTranslationKey: "sort_with_photos",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                },
                {
                    value: "mostHelpful",
                    labelTranslationKey: "sort_most_helpful",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                },
            ];

            if (settings?.isReviewsPinningEnabled) {
                sortItems.unshift({
                    value: "mostRelevant",
                    labelTranslationKey: "sort_most_relevant",
                    labelTranslationNamespace: "reviews.ReviewsPageWidget",
                });
            }

            return [...sortItems];
        })
    );

    const t_search$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsPageWidget",
        "search"
    );
    const t_no_reviews_message$ = i18nModule.exports.i18n.selectT(
        "reviews.ReviewsPageWidget",
        "no_reviews_message"
    );
    const t_write_a_review$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsPageWidget",
        "write_a_review"
    );
    const t_load_more$ = i18nModule.exports.i18n.selectT(
        "reviews.ReviewsPageWidget",
        "load_more"
    );
    const t_no_reviews_message_search_result$ = i18nModule.exports.i18n.selectT(
        "reviews.ReviewsPageWidget",
        "no_reviews_message_search_result"
    );
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <div class="gw-rv-reviews-page__wrapper">
            {#if $reviewsSettings$}
                <div class="gw-rv-reviews-page">
                    <div class="gw-rv-reviews-page__title-block">
                        <h2 class="gw-rv-reviews-page__title">
                            <T
                                namespace="reviews.critical.ReviewsPageWidget"
                                translationKey="customer_reviews_title"
                            />
                        </h2>
                        <Button
                            variant="opacityOnHover"
                            class="gw-rv-reviews-page__add-review-btn"
                            size="large"
                            on:click={handleOpenReviewsFormModal}
                        >
                            <T
                                namespace="reviews.critical.ReviewsPageWidget"
                                translationKey="write_a_review"
                            />
                        </Button>
                    </div>

                    <Tabs
                        tabsName="reviews-page-tab"
                        class="gw-rv-reviews-page__tab"
                    >
                        <div class="gw-rv-reviews-page__header" slot="menu">
                            <div class="gw-rv-reviews-page__header-tabs">
                                {#if $tabs$}
                                    <TabsMenu
                                        {tabsManager}
                                        tabs={$tabs$}
                                        position="start"
                                    />
                                {/if}
                            </div>
                            <div class="gw-rv-reviews-page__header-actions">
                                <div class="gw-rv-reviews-page__search">
                                    <ReviewsTextInput
                                        withClearIcon
                                        ariaLabel="Search review"
                                        placeholder={$t_search$}
                                        value={$search$}
                                        on:change={handleSearch}
                                    />
                                </div>

                                <div class="gw-rv-reviews-page__sort">
                                    <Sort
                                        sortItems={$sortItems$}
                                        currentSortValue={$currentSort$}
                                        on:sortItems={(event) =>
                                            handleSortReview(
                                                event.detail.value
                                            )}
                                    >
                                        <T
                                            namespace="reviews.critical.ReviewsPageWidget"
                                            translationKey="sort_by"
                                        />
                                    </Sort>

                                    <ReviewLayoutSwitcher
                                        {layout}
                                        on:changeLayout={handleChangeLayoutView}
                                    />
                                </div>
                            </div>
                        </div>

                        <Divider />

                        <svelte:fragment slot="body">
                            <TabsPanel
                                {tabsManager}
                                name={REVIEWS_PAGE_TABS.PRODUCT_REVIEWS}
                            >
                                <div class="gw-rv-reviews-page__main">
                                    {#if $productReviews$.length === 0 && $productReviewsStatus$ === STATUS.READY && $search$.length > 0}
                                        <div
                                            class="gw-rv-reviews-page__empty-list-wrapper"
                                        >
                                            <ReviewsEmptyList
                                                title={$t_no_reviews_message_search_result$}
                                            >
                                                <Icon
                                                    slot="icon"
                                                    width={100}
                                                    height={80}
                                                    viewBox="0 0 100 84"
                                                >
                                                    <ReviewsEmptyListBackgroundIcon
                                                    />
                                                </Icon>
                                            </ReviewsEmptyList>
                                        </div>
                                    {:else if $productReviews$.length === 0 && $productReviewsStatus$ === STATUS.READY}
                                        <div
                                            class="gw-rv-reviews-page__empty-list-wrapper"
                                        >
                                            <ReviewsEmptyList
                                                title={$t_no_reviews_message$}
                                                actionText={$t_write_a_review$}
                                                on:action={handleOpenReviewsFormModal}
                                            >
                                                <Icon
                                                    slot="icon"
                                                    width={100}
                                                    height={80}
                                                    viewBox="0 0 100 84"
                                                >
                                                    <ReviewsEmptyListBackgroundIcon
                                                    />
                                                </Icon>
                                            </ReviewsEmptyList>
                                        </div>
                                    {:else}
                                        {#if layout === REVIEWS_WIDGET_LAYOUT.GRID_LAYOUT}
                                            <ReviewsMasonryList
                                                reviews={$productReviews$}
                                                status={$productReviewsStatus$}
                                                namespace="reviews.ReviewsPageWidget"
                                                on:openReview={handleOpenReviewsGalleryModal}
                                                votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                                                showTitle={$reviewsSettings$.isReviewTitleEnabled}
                                                isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                                                showProducts
                                            />
                                        {:else}
                                            <ReviewsList
                                                reviews={$productReviews$}
                                                status={$productReviewsStatus$}
                                                namespace="reviews.ReviewsPageWidget"
                                                showTitle={$reviewsSettings$.isReviewTitleEnabled}
                                                votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                                                isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                                                showProducts
                                                on:openReview={handleOpenReviewsGalleryModal}
                                            />
                                        {/if}
                                        {#if hasNextPage($productReviewsPaginationMeta$)}
                                            <LoadMore
                                                variant="opacityOnHover"
                                                text={$t_load_more$}
                                                ariaLabel="load more button"
                                                on:loadMore={handleProductReviewsLoadMore}
                                                loading={$productReviewsStatus$ ===
                                                    STATUS.LOADING}
                                            />
                                        {/if}
                                    {/if}
                                </div>
                            </TabsPanel>
                            <TabsPanel
                                {tabsManager}
                                name={REVIEWS_PAGE_TABS.SITE_REVIEWS}
                            >
                                <div class="gw-rv-reviews-page__main">
                                    {#if $siteReviews$.length === 0 && $siteReviewsStatus$ === STATUS.READY && $search$.length > 0}
                                        <div
                                            class="gw-rv-reviews-page__empty-list-wrapper"
                                        >
                                            <ReviewsEmptyList
                                                title={$t_no_reviews_message_search_result$}
                                            >
                                                <Icon
                                                    slot="icon"
                                                    width={100}
                                                    height={80}
                                                    viewBox="0 0 100 84"
                                                >
                                                    <ReviewsEmptyListBackgroundIcon
                                                    />
                                                </Icon>
                                            </ReviewsEmptyList>
                                        </div>
                                    {:else if $siteReviews$.length === 0 && $siteReviewsStatus$ === STATUS.READY}
                                        <div
                                            class="gw-rv-reviews-page__empty-list-wrapper"
                                        >
                                            <ReviewsEmptyList
                                                title={$t_no_reviews_message$}
                                                actionText={$t_write_a_review$}
                                                on:action={handleOpenReviewsFormModal}
                                            >
                                                <Icon
                                                    slot="icon"
                                                    width={100}
                                                    height={80}
                                                    viewBox="0 0 100 84"
                                                >
                                                    <ReviewsEmptyListBackgroundIcon
                                                    />
                                                </Icon>
                                            </ReviewsEmptyList>
                                        </div>
                                    {:else}
                                        {#if layout === REVIEWS_WIDGET_LAYOUT.GRID_LAYOUT}
                                            <ReviewsMasonryList
                                                reviews={$siteReviews$}
                                                status={$siteReviewsStatus$}
                                                namespace="reviews.ReviewsPageWidget"
                                                on:openReview={handleOpenReviewsGalleryModal}
                                                votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                                                showTitle={$reviewsSettings$.isReviewTitleEnabled}
                                                isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                                                showProducts={false}
                                            />
                                        {:else}
                                            <ReviewsList
                                                reviews={$siteReviews$}
                                                status={$siteReviewsStatus$}
                                                showTitle={$reviewsSettings$.isReviewTitleEnabled}
                                                votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                                                isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                                                showProducts={false}
                                                namespace="reviews.ReviewsPageWidget"
                                                on:openReview={handleOpenReviewsGalleryModal}
                                            />
                                        {/if}
                                        {#if hasNextPage($siteReviewsPaginationMeta$)}
                                            <LoadMore
                                                variant="opacityOnHover"
                                                text={$t_load_more$}
                                                ariaLabel="load more button"
                                                on:loadMore={handleSiteReviewsLoadMore}
                                                loading={$siteReviewsStatus$ ===
                                                    STATUS.LOADING}
                                            />
                                        {/if}
                                    {/if}
                                </div>
                            </TabsPanel>
                        </svelte:fragment>
                    </Tabs>
                    <GrowaveBrand
                        class="gw-rv-reviews-page__growave-brand"
                        canShowWatermark={$watermarkSettings$?.canShowWatermark}
                    />
                    <ImageViewer {imageViewerController} />
                </div>
            {/if}
        </div>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-reviews-page {
        max-width: 1240px;
        margin: 0 auto;

        --gw-tabs-underline-color: var(--gw-rv-theme-primaryColor);

        &__wrapper {
            margin-left: calc(-50vw + 50%);
            margin-right: calc(-50vw + 50%);
            padding: 0 40px;

            @media all and (max-width: 700px) {
                margin-right: 0;
                margin-left: 0;
                padding: 0 20px;
            }

            @media all and (max-width: 500px) {
                padding: 0;
            }
        }

        @media all and (max-width: 960px) {
            padding: 0 0.9em;
        }

        &__main {
            display: flex;
            flex-direction: column;
            gap: 3em;
            padding-top: 2em;
        }

        &__form-wrapper {
            padding-top: 2em;
        }

        &__title {
            margin: 0;
            font-size: 1.8667em;
            font-weight: bold;
            text-align: left;

            @media all and (max-width: 500px) {
                width: 100%;
                text-align: center;
            }

            &-block {
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                row-gap: 1em;
                margin-bottom: 1.125em;
            }
        }

        &__header {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 0.75em;
        }

        &__header-tabs {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        &__header-actions {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding-top: 12px;
            align-items: center;

            .gw-btn.gw-button.--outlined {
                color: var(--gw-theme-main-text-primary);
                &:hover,
                &:focus,
                &:focus-within {
                    opacity: 0.8;
                    background: none;
                }
            }

            @media all and (max-width: 576px) {
                flex-direction: column;
                gap: 0.4em;
            }
        }

        &__search {
            max-width: 260px;
            width: 100%;

            @media all and (max-width: 960px) {
                margin-bottom: 1em;
            }

            @media all and (max-width: 650px) {
                max-width: 100%;
            }
        }

        &__search-input {
            width: 100%;
            padding: 0.5em 1em;
            border: 1px solid silver;
            border-radius: 2px;
            font-size: 0.875em;
        }

        &__sort {
            display: flex;
            align-items: center;
            gap: 0.75em;
            margin-left: auto;
        }

        &__count {
            display: flex;
            align-items: center;
            column-gap: 0.5em;

            &-text {
                font-size: 0.875em;
                line-height: 0.875em;
            }
        }

        &__growave-brand {
            margin-block: var(--gw-space-9);
            text-align: right;
        }

        &__empty-list-wrapper {
            height: 50vh;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .gw-btn.gw-rv-reviews-page__add-review-btn {
        margin-left: auto;
    }
</style>
